import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import type { StreamChat } from "stream-chat";
import {
  PushRegistrationTimeoutError,
  getChatPushPreference,
  registerChatPushDevice,
} from "Infrastructure/Capacitor/chatPushDevice";

/**
 * Keeps this phone registered with Stream for whichever persona the shared chat client is
 * connected as. Re-runs per client, i.e. per persona switch (the connect path has already removed
 * the device from the previous persona by then). Runs on every launch because push tokens can
 * rotate. Silent unless the user opted in ("on"); the chat gate handles the first prompt. Never
 * throws: a failed registration is a Sentry event, not a broken app — a token timeout (offline
 * launch, retried next launch anyway) only a warning, so it cannot page anyone.
 * Native shells only — the caller mounts it behind `isNativePlatform()`.
 */
export const useChatPushRegistration = (client: StreamChat | undefined) => {
  useEffect(() => {
    if (!client || getChatPushPreference() !== "on") {
      return;
    }

    let isCancelled = false;

    void registerChatPushDevice(client).catch((error) => {
      if (isCancelled) {
        return;
      }
      Sentry.captureException(error, {
        tags: { feature: "chatPush" },
        level:
          error instanceof PushRegistrationTimeoutError ? "warning" : "error",
      });
    });

    return () => {
      isCancelled = true;
    };
  }, [client]);
};
